import { useAccount } from '@hooks/useAccount';
import { type AppPage } from '@hooks/useNavigation';
import { useUI } from '@hooks/useUI';
import { useRoundTimer } from '@hooks/useRoundTimer';
import { colors } from '@utils/design-tokens';
import { formatPrice } from '@utils/price-formatting';

interface HeaderProps {
  asset: string;
  onNavigate: (page: AppPage) => void;
  title?: string;
  showCountdown?: boolean;
  onClickAddFunds?: () => void;
}

export function Header({
  asset,
  onNavigate,
  title,
  showCountdown = false,
  onClickAddFunds,
}: HeaderProps) {
  const { accountValue, positions, openPnl } = useAccount();
  const { showSideMenu, toggleSideMenu, openDepositTray } = useUI();
  const { formatTime, timeLeft } = useRoundTimer();
  
  // Format time string - using timeLeft ensures component re-renders when it changes
  const countdownText = timeLeft ? formatTime() : '00:00:00';

  const handleAddFunds = () => {
    if (onClickAddFunds) {
      onClickAddFunds();
    } else {
      openDepositTray();
    }
  };

  return (
    <div className="w-full">
      <div className="flex border-b border-divider">
        {/* Left section - Menu and Market Display */}
        <div className="border-r border-divider pl-3 pr-5 py-2 flex items-center gap-2">
          <button
            className="p-1 hover:scale-105 active:scale-95 transition-transform cursor-pointer flex-shrink-0"
            onClick={toggleSideMenu}
          >
            <div className="w-6 h-6">
              {showSideMenu ? (
                <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="#FAFBFB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3.5 7V5.5H20.5V7H3.5ZM3.5 18.5V17H20.5V18.5H3.5ZM3.5 12.75V11.25H20.5V12.75H3.5Z"
                    fill="#FAFBFB"
                  />
                </svg>
              )}
            </div>
          </button>
          <h1
            className={`${colors.primary} text-base font-semibold min-w-0 whitespace-nowrap`}
          >
            {showCountdown ? countdownText : (title ?? `${asset} MARKET`)}
          </h1>
        </div>

        {/* Center section - PnL Display */}
        <div
          className={
            'flex-1 border-r border-divider px-1 py-2 flex items-center justify-center'
          }
        >
          <button
            className="hover:scale-105 transition-transform cursor-pointer"
            onClick={() => onNavigate('positions')}
          >
            <span
              className={`text-[14px] font-semibold ${
                positions.length > 0
                  ? openPnl === 0
                    ? colors.secondary
                    : openPnl >= 0
                      ? colors.success
                      : colors.danger
                  : colors.secondary
              }`}
            >
              {positions.length === 0 ? (
                <span className={'text-xs text-center whitespace-nowrap'}>
                  no positions
                </span>
              ) : (
                `${openPnl === 0 ? '-' : openPnl > 0 ? `+$${formatPrice(Math.abs(openPnl))}` : `-$${formatPrice(Math.abs(openPnl))}`}`
              )}
            </span>
          </button>
        </div>

        {/* Right section - Account Value */}
        <div className="flex-1 pl-5 pr-3 py-2 flex items-center justify-between gap-3">
          <span className={`${colors.primary} text-[14px] font-semibold`}>
            ${formatPrice(accountValue)}
          </span>
          <button
            className="w-5 h-5 rounded-sm border border-[#828699] flex items-center justify-center hover:scale-110 active:scale-90 transition-transform cursor-pointer ml-2 flex-shrink-0"
            onClick={handleAddFunds}
          >
            <svg className="w-2.5 h-2.5" viewBox="0 0 12 12" fill="none">
              <path
                d="M5.5 10.5V6.5H1.5V5.5H5.5V1.5H6.5V5.5H10.5V6.5H6.5V10.5H5.5Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
